/**
 * Knowledge folder + document helpers used by the ConnectionsPage UI.
 *
 * Folders / documents CRUD goes through RLS-protected supabase queries directly. Uploads
 * are two-step: the file is pushed straight to Supabase Storage with the user session,
 * then the edge function materializes the document row with extracted text + summary.
 */

import { supabase } from './supabase';
import type {
  KnowledgeDocument,
  KnowledgeFolder,
  KnowledgeVisibility,
} from '@shared/types/knowledge';

const STORAGE_BUCKET = 'knowledge';

export interface KnowledgeFolderWithCount extends KnowledgeFolder {
  document_count: number;
}

export const listKnowledgeFolders = async (): Promise<KnowledgeFolderWithCount[]> => {
  const { data, error } = await supabase
    .from('knowledge_folders')
    .select('*, knowledge_documents(count)')
    .is('deleted_at', null)
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => {
    const { knowledge_documents, ...rest } = row as Record<string, unknown> & {
      knowledge_documents?: Array<{ count: number }> | null;
    };
    const document_count = knowledge_documents && knowledge_documents[0]
      ? knowledge_documents[0].count
      : 0;
    return { ...(rest as unknown as KnowledgeFolder), document_count };
  });
};

export const createKnowledgeFolder = async (params: {
  name: string;
  description?: string;
  kind: KnowledgeVisibility;
  tenantId: string;
  userId: string;
}): Promise<KnowledgeFolder> => {
  const { data, error } = await supabase
    .from('knowledge_folders')
    .insert({
      name: params.name,
      description: params.description ?? null,
      kind: params.kind,
      tenant_id: params.tenantId,
      owner_user_id: params.userId,
    })
    .select('*')
    .single();
  if (error || !data) throw new Error(error?.message ?? 'Création impossible');
  return data as KnowledgeFolder;
};

export const deleteKnowledgeFolder = async (folderId: string): Promise<void> => {
  const { error } = await supabase
    .from('knowledge_folders')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', folderId);
  if (error) throw new Error(error.message);
};

export const listFolderDocuments = async (folderId: string): Promise<KnowledgeDocument[]> => {
  const { data, error } = await supabase
    .from('knowledge_documents')
    .select('*')
    .eq('folder_id', folderId)
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as KnowledgeDocument[];
};

export const deleteKnowledgeDocument = async (documentId: string): Promise<void> => {
  const { error } = await supabase
    .from('knowledge_documents')
    .delete()
    .eq('id', documentId);
  if (error) throw new Error(error.message);
};

const slugify = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80) || 'document';

const guessExtension = (file: File): string => {
  const fromName = file.name.split('.').pop();
  if (fromName && fromName.length <= 8) return fromName.toLowerCase();
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'text/markdown') return 'md';
  return 'txt';
};

interface UploadDocumentResult {
  document: Pick<KnowledgeDocument, 'id' | 'folder_id' | 'title' | 'source_kind' | 'summary' | 'byte_size' | 'char_count' | 'created_at' | 'updated_at'>;
}

const invokeUploadDocument = async (body: Record<string, unknown>): Promise<UploadDocumentResult> => {
  const { data, error } = await supabase.functions.invoke<UploadDocumentResult>(
    'knowledge-upload-document',
    { body },
  );
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Réponse vide du service d\'upload');
  return data;
};

export const uploadKnowledgeFileDocument = async (params: {
  folderId: string;
  title: string;
  file: File;
  userId: string;
}): Promise<UploadDocumentResult> => {
  const ext = guessExtension(params.file);
  const storageKey = `${params.userId}/${params.folderId}/${crypto.randomUUID()}-${slugify(params.file.name)}.${ext}`;
  const { error: uploadError } = await supabase
    .storage
    .from(STORAGE_BUCKET)
    .upload(storageKey, params.file, {
      contentType: params.file.type || undefined,
      upsert: false,
    });
  if (uploadError) throw new Error(uploadError.message);

  return await invokeUploadDocument({
    folder_id: params.folderId,
    title: params.title,
    source_kind: 'upload',
    storage_key: storageKey,
    mime_type: params.file.type || undefined,
  });
};

export const uploadKnowledgeInlineDocument = async (params: {
  folderId: string;
  title: string;
  content: string;
}): Promise<UploadDocumentResult> => {
  return await invokeUploadDocument({
    folder_id: params.folderId,
    title: params.title,
    source_kind: 'inline',
    content: params.content,
  });
};

// =============================================================================
// Flow attachments
// =============================================================================
